import React, {
	useEffect,
	useRef,
	useState,
} from "react";
import classNames from 'classnames';

import IndeterminateCheckbox from "components/uiComponents/IndeterminateCheckbox";
import { setCurrentTableCellAC } from 'store/actions/table';

import { useDispatch, useSelector } from "react-redux";
import useOutsideClick from "hooks/useOutSideClick";
import { valueTypes } from "utils/builder/ResultClasses";

const MultipleChoice = (props) => {
	const { choicesState, setChoicesState, data } = props;

	const dispatch = useDispatch();
	const customSelectRef = useRef();

	const [customPropertiesValueState, setCustomPropertiesValueState] = useState([]);
	const [isOpend, setIsOpend] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({});

	const { customPropertiesValues } = useSelector((state) => state.viewer);

	const multiplechoiceValuesFiltered =
		customPropertiesValues?.find((item) => item?.name === choicesState?.columnId) || {
			name: choicesState?.columnId,
			values: choicesState?.existingValues,
		};
	const multiplechoiceValues = multiplechoiceValuesFiltered?.values;

const isCheckboxesDisabled = (
  choicesState?.type === valueTypes.customEnumSingle ||
  choicesState?.type === valueTypes.proEnumSingle
) && !!customPropertiesValueState.length;

	useEffect(() => {
		const activeValue = data?.[choicesState?.rowIndex]?.[multiplechoiceValuesFiltered?.name];
		if (!!activeValue) {
			const activeValueArray = activeValue?.split("; ")

			if (!!activeValueArray?.length) {
				setCustomPropertiesValueState(activeValueArray)
			};
		};

		if (choicesState) {
			setIsOpend(true);
		};
	}, [choicesState])

	useEffect(() => {
		if (!choicesState) {
			setDropdownPosition({});
			return;
		}

		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const clickY = choicesState.y;
		const clickX = choicesState.x;
		
		if (typeof clickY !== 'number' || typeof clickX !== 'number') {
			const fallbackBottom = choicesState?.y ? window?.innerHeight - choicesState?.y : 'auto';
			setDropdownPosition({
				bottom: fallbackBottom,
				top: 'auto',
				insetInlineStart: choicesState?.x || 0,
				insetInlineEnd: 'auto'
			});
			return;
		}
		
		const estimatedItemHeight = 35;
		const itemCount = (multiplechoiceValues?.length || 0) + 1;
		const estimatedDropdownHeight = Math.min(itemCount * estimatedItemHeight + 20, 180);
		
		const estimatedCellHeight = 50;
		const cellBottom = clickY + estimatedCellHeight;
		
		const spaceBelow = viewportHeight - cellBottom;
		const dropdownWidth = 180;
		const gap = 5;
		const margin = 10;
		
		const minSpaceNeeded = estimatedDropdownHeight + gap + margin;
		const hasEnoughSpaceBelow = spaceBelow >= minSpaceNeeded;
		
		let verticalPosition = {};
		if (hasEnoughSpaceBelow) {
			const dropdownTop = cellBottom + gap;
			const dropdownBottom = dropdownTop + estimatedDropdownHeight;
			const bottomPosition = viewportHeight - dropdownBottom;
			verticalPosition = { 
				bottom: Math.max(margin, bottomPosition), 
				top: 'auto' 
			};
		} else {
			const topPosition = clickY - estimatedDropdownHeight - gap;
			verticalPosition = { 
				top: Math.max(margin, topPosition), 
				bottom: 'auto' 
			};
		}
		
		let horizontalPosition = {};
		if (clickX + dropdownWidth > viewportWidth - margin) {
			horizontalPosition = { 
				insetInlineStart: 'auto', 
				insetInlineEnd: Math.max(margin, viewportWidth - clickX - dropdownWidth)
			};
		} else {
			horizontalPosition = { 
				insetInlineStart: Math.max(margin, clickX), 
				insetInlineEnd: 'auto'
			};
		}
		
		setDropdownPosition({
			...verticalPosition,
			...horizontalPosition
		});
	}, [choicesState, multiplechoiceValues])

	const handleCheckboxChange = (isChecked, value) => {
		if (isChecked) {
			setCustomPropertiesValueState((prevState) => [...prevState, value]);
		} else {
			setCustomPropertiesValueState((prevState) =>
				prevState.filter((item) => item !== value)
			);
		};
	};

	const handleSetValue = ({ value }) => {
		const columnId = choicesState?.columnId;
		const rowIndex = choicesState?.rowIndex;

		dispatch(setCurrentTableCellAC(columnId, data[rowIndex]?.lineElements, value));
		setChoicesState(null);
		setCustomPropertiesValueState([]);
	};

	useOutsideClick(customSelectRef, () => {
		const newValue = customPropertiesValueState
			.map(
				(item, i) =>
					item +
					`${customPropertiesValueState.length > 1 &&
						i !== customPropertiesValueState.length - 1
						? "; "
						: ""
					}`
			)
			.join("");

		if (isOpend) {
			setChoicesState(null);
			setIsOpend(false);
		};

		if (!!newValue) {
			handleSetValue({ value: newValue });
		};
	});

	const positionStyle = choicesState && Object.keys(dropdownPosition).length > 0
		? dropdownPosition
		: choicesState
			? { bottom: window?.innerHeight - choicesState?.y, insetInlineStart: choicesState?.x }
			: {};

	return (
		<div
			ref={customSelectRef}
			className="checkbox-dropdown"
			style={{ 
				display: choicesState ? 'initial' : 'none', 
				...positionStyle
			}}
		>
			{multiplechoiceValues?.map((elem, index) => {
				return (
					<div
						key={`${elem}-${index}`}
						className={classNames({
							"checkbox-dropdown__item": true,
							"checkbox-dropdown__item-disabled": isCheckboxesDisabled && !customPropertiesValueState.includes(elem),
						})}
					>
						<IndeterminateCheckbox
							checked={!!customPropertiesValueState.includes(elem)}
							label={elem}
							handleChange={(e) =>
								handleCheckboxChange(e.target.checked, elem)
							}
							disabled={isCheckboxesDisabled && !customPropertiesValueState.includes(elem)}
						/>
					</div>
				);
			})}
					<div className="clear-select-value" onClick={() => handleSetValue({value: ""})}>Clear value</div>
		</div>
	);
};

export default MultipleChoice;